import { Badge, Button, Card, CardBody, Col, Container, ListGroup, Row, Spinner } from 'react-bootstrap';
import { useServicesPage } from '@/hooks/useServicesPage';
import { getMediaUrl } from '@/utils/imageUrl';

const TourOperatorServices = ({ sectionData: propSectionData }) => {
  const { section: fetchedSection, loading, error } = useServicesPage('tour_operator_services');
  const section = propSectionData || fetchedSection;

  const bgColor = section?.background_color || 'light';
  const bgEffect = section?.background_effect || null;
  let bgClass = bgColor === 'primary' ? 'section-bg-primary' : bgColor === 'white' ? 'bg-white' : 'bg-light';
  // Apply gradient effect if selected
  if (bgColor === 'primary' && bgEffect === 'gradient') {
    bgClass = 'section-bg-primary-gradient';
  }

  if (loading && !propSectionData) {
    return (
      <section id="tour-operator-services" className={`py-5 py-lg-6 ${bgClass} services-section tour-operator-services-section`}>
        <Container>
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        </Container>
      </section>
    );
  }

  if (error || !section || !section.is_active) {
    return null;
  }

  const serviceHighlights = section.items || [];
  const valuePoints = section.content?.value_points || [];
  const hasDotsEffect = bgColor === 'primary' && bgEffect === 'dots';
  const introTextRaw = section.intro_text || '';
  const introTextSanitized = introTextRaw.replace(/<(?!br\s*\/?)[^>]+>/gi, '');
  const introTextHtml = introTextSanitized.replace(/\n/g, '<br />');
  const defaultButton = {
    label: 'Discuss your program',
    url: '/contact',
    color: 'primary',
    variant: 'solid',
    IconComponent: null
  };
  const sectionButton = section.content?.button || defaultButton;

  return (
    <section id="tour-operator-services" className={`py-5 py-lg-6 ${bgClass} services-section tour-operator-services-section ${hasDotsEffect ? 'position-relative overflow-hidden' : ''}`}>
      {hasDotsEffect && (
        <>
          <span className="section-bg-dots section-bg-dots--left d-none d-xl-block" />
          <span className="section-bg-dots section-bg-dots--right d-none d-lg-block" />
        </>
      )}
      <Container className={hasDotsEffect ? 'position-relative' : ''}>
        <Row className="g-4 align-items-start">
          <Col lg={5}>
            {section.subtitle && (
              <Badge bg="primary" className="bg-opacity-10 text-primary">
                {section.subtitle}
              </Badge>
            )}
            {section.title && (
              <h2 className="sec-title mb-3">{section.title}</h2>
            )}
            {introTextRaw && (
              <p
                className="lead intro-text"
                dangerouslySetInnerHTML={{ __html: introTextHtml }}
              />
            )}
            {valuePoints.length > 0 && (
              <ListGroup as="div" className="mb-4 border-0">
                {valuePoints.map((point, idx) => (
                  <ListGroup.Item
                    key={idx}
                    className="border-0 ps-0 d-flex align-items-start bg-transparent"
                    style={{ color: '#495057' }}
                  >
                    <span className="badge rounded-pill bg-primary-subtle text-primary me-3 mt-1">
                      •
                    </span>
                    <span style={{ fontWeight: '500' }}>{point}</span>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
            <div className="d-flex flex-wrap gap-3">
              <Button
                href={sectionButton.url}
                size="lg"
                variant={sectionButton.variant === 'outline' ? `outline-${sectionButton.color || 'primary'}` : (sectionButton.color || 'primary')}
              >
                {sectionButton.label}
                {sectionButton.IconComponent && <sectionButton.IconComponent className="ms-2" />}
              </Button>
            </div>
          </Col>
          <Col lg={7}>
            <Row className="g-4">
              {serviceHighlights.map((item, idx) => {
                const Icon = item.IconComponent;
                const backgroundImage = getMediaUrl(item.background_image);
                
                return (
                  <Col key={item.id || idx} sm={6}>
                    <Card className="h-100 border-0 shadow-sm card-hover-shadow service-item-card">
                      {backgroundImage ? (
                        <div 
                          className="service-item-header"
                          style={{
                            position: 'relative',
                            width: '100%',
                            aspectRatio: '4/3',
                            overflow: 'hidden',
                            backgroundImage: `url(${backgroundImage})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            borderRadius: '0.5rem 0.5rem 0 0'
                          }}
                        >
                          {/* Gradient overlay */}
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.1) 50%, transparent 100%)',
                              zIndex: 1
                            }}
                          />
                          {/* Icon overlay */}
                          {Icon && (
                            <div 
                              className="icon-lg text-primary rounded-circle flex-centered"
                              style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 2,
                                width: '80px',
                                height: '80px',
                                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                              }}
                            >
                              <Icon size={32} />
                            </div>
                          )}
                        </div>
                      ) : (
                        Icon && (
                          <div className="icon-lg bg-primary bg-opacity-10 text-primary rounded-circle flex-centered mb-3" style={{ margin: '1.5rem 1.5rem 0 1.5rem' }}>
                            <Icon size={32} />
                          </div>
                        )
                      )}
                      <CardBody className="p-4">
                        {item.title && <h5 className="mb-2 fw-bold">{item.title}</h5>}
                        {item.description && <p className="mb-0" style={{ color: '#495057', lineHeight: '1.6' }} dangerouslySetInnerHTML={{ __html: item.description }} />}
                      </CardBody>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default TourOperatorServices;

